import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { submitCaseToSF } from '../../../actions/hub/hubContactPageActions';
import ErrorBlock from '../../../common/ErrorBlock';
import { ContainerLayout } from '../../../common/hub/ContainerLayout';
import withSelectedEnrolment from '../../../common/hub/NewWithSelectedEnrolment';
import { RestrictedButton } from '../../../components/RestrictedButton/RestrictedButton';
import { ShadowContainer } from '../../../components/ShadowContainer/ShadowContainer';
import { TextArea, useTextAreaProps } from '../../../components/TextArea/TextArea';
import FormSubHeading from '../../../components/hub/contact/FormSubHeading';
import { PATH_HUB_CR_PAYMENT_QUERY, PATH_HUB_CR_SUBMIT_CASE_SUCCESS } from '../../../util/pagePath';
import { PATH_HUB_CR_REPORT_ISSUE } from '../../../util/pagePath';
import { usePageHeader } from '../../../v2/app-shell/usePageHeader';
import { StudentEnrolmentDetails } from './StudentEnrolmentDetails';

const FormPaymentQuery = ({ selectedEnrolment, isSubmitting, submitError, errorMsg, submit }) => {
  const history = useHistory();
  const { setHeader } = usePageHeader();
  const { enrolmentId, studentSfid, student, subject } = selectedEnrolment;
  const location = useLocation();
  const headerLabel = location.state?.headerLabel ?? 'Report an issue';

  useEffect(() => {
    setHeader({
      label: headerLabel,
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader, headerLabel]);

  const textAreaProps = useTextAreaProps({
    id: 'text-how-can-we-help',
    labelText: 'How can we help you?',
  });

  const message = textAreaProps.value;

  const onClickHandler = () => {
    submit({
      uniqueEnrolmentId: enrolmentId,
      message: `Payment query. Other details: ${message}`,
      caseSubject: 'Contact request - Payment - Support Request',
      caseType: 'Payment',
      caseCategory: 'Support Request',
      onSubmitSuccess: () => {
        history.push(PATH_HUB_CR_SUBMIT_CASE_SUCCESS, {
          firstName: student,
          studentId: studentSfid,
          header: 'Query submitted',
          message: `Your payment query for ${student}'s ${subject} enrolment has been submitted successfully. We'll be in touch soon.`,
          headerLabel: 'Report an issue',
          headerBackPath: `${PATH_HUB_CR_REPORT_ISSUE}?token=${enrolmentId}`,
        });
      },
    });
  };

  return (
    <ContainerLayout>
      <ShadowContainer className="flex flex-col gap-y-10 !rounded-3xl bg-white p-6 md:!p-20">
        <div className="flex flex-col gap-y-5">
          <FormSubHeading />
          {submitError && errorMsg && <ErrorBlock errorMsg={errorMsg} />}
          <StudentEnrolmentDetails student={student} subject={subject} selectedEnrolment={selectedEnrolment} />
        </div>
        <form>
          <TextArea {...textAreaProps} />
        </form>
        <div className="text-center tracking-normal">
          <RestrictedButton
            buttonText="Submit query"
            loading={true}
            truthyChecks={[message.length > 0, !isSubmitting]}
            onClick={() => {
              onClickHandler();
            }}
          />
        </div>
      </ShadowContainer>
    </ContainerLayout>
  );
};

FormPaymentQuery.propTypes = {
  selectedEnrolment: PropTypes.shape({
    enrolmentId: PropTypes.string.isRequired,
    detail: PropTypes.string.isRequired,
    nextSession: PropTypes.string.isRequired,
    studentSfid: PropTypes.string.isRequired,
    student: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  submitError: PropTypes.bool.isRequired,
  errorMsg: PropTypes.string.isRequired,
  submit: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    return {
      isSubmitting: state.ui.apiState.hubContactPage.isLoading,
      submitError: state.ui.apiState.hubContactPage.fetchError,
      errorMsg: state.ui.apiState.hubContactPage.errorMsg,
    };
  },
  (dispatch) => {
    return {
      submit: bindActionCreators(submitCaseToSF, dispatch),
    };
  }
)(withSelectedEnrolment(FormPaymentQuery, null, PATH_HUB_CR_PAYMENT_QUERY));
